/**
 * Prompt Dialog Composable
 * Promise-based prompt dialog with text input.
 *
 * Usage:
 * ```ts
 * const prompt = usePrompt()
 * const value = await prompt({
 *   title: 'Commit Message',
 *   message: 'Enter a commit message for this suggestion:',
 *   placeholder: 'Apply suggestion from code review',
 *   defaultValue: 'Apply suggestion from code review',
 *   confirmText: 'Commit',
 * })
 * if (value !== null) { // user confirmed
 *   doSomething(value)
 * }
 * ```
 */

import { reactive, readonly, ref } from 'vue'

export interface PromptOptions {
  title?: string
  message?: string
  placeholder?: string
  defaultValue?: string
  confirmText?: string
  cancelText?: string
  inputType?: 'text' | 'textarea'
  loading?: boolean
}

interface PromptState {
  visible: boolean
  loading: boolean
  options: PromptOptions
  resolve: ((value: string | null) => void) | null
}

export const promptInputValue = ref('')

const state = reactive<PromptState>({
  visible: false,
  loading: false,
  options: {
    title: 'Input',
    message: '',
    placeholder: '',
    defaultValue: '',
    confirmText: 'OK',
    cancelText: 'Cancel',
    inputType: 'text',
  },
  resolve: null,
})

function openPrompt(options: PromptOptions): Promise<string | null> {
  if (state.resolve) {
    state.resolve(null)
  }

  return new Promise<string | null>((resolve) => {
    state.options = {
      title: options.title ?? 'Input',
      message: options.message ?? '',
      placeholder: options.placeholder ?? '',
      defaultValue: options.defaultValue ?? '',
      confirmText: options.confirmText ?? 'OK',
      cancelText: options.cancelText ?? 'Cancel',
      inputType: options.inputType ?? 'text',
    }
    promptInputValue.value = options.defaultValue ?? ''
    state.loading = options.loading ?? false
    state.resolve = resolve
    state.visible = true
  })
}

function handleConfirm() {
  if (state.resolve) {
    state.resolve(promptInputValue.value)
    state.resolve = null
  }
  state.visible = false
}

function handleCancel() {
  if (state.resolve) {
    state.resolve(null)
    state.resolve = null
  }
  state.visible = false
}

function updateInputValue(value: string) {
  promptInputValue.value = value
}

function setLoading(value: boolean) {
  state.loading = value
}

function updateDefaultValue(value: string) {
  promptInputValue.value = value
  state.options.defaultValue = value
}

export function usePrompt() {
  const promptFn = (options: PromptOptions) => openPrompt(options)

  promptFn.state = readonly(state)
  promptFn.handleConfirm = handleConfirm
  promptFn.handleCancel = handleCancel
  promptFn.updateInputValue = updateInputValue
  promptFn.setLoading = setLoading
  promptFn.updateDefaultValue = updateDefaultValue

  return promptFn
}

export const promptState = readonly(state)
export {
  handleConfirm as handlePromptConfirm,
  handleCancel as handlePromptCancel,
  updateInputValue,
  setLoading as setPromptLoading,
  updateDefaultValue as updatePromptDefaultValue,
}
